import "react-day-picker/dist/style.css";

import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { updatePatient } from "~/api/patients";
import type { Patient } from "~/api/patients";
import type { Unit } from "~/api/units";
import { getUnits } from "~/api/units";
import {
  Button,
  DatePicker,
  Modal,
  SearchableSingleSelect,
  UneditableInput,
  Warning,
} from "~/ui";
import { formatBackendDate, parseBackendDate } from "~/utils";
import { errorToast, useToastStore } from "../../Shared/Toast";

interface EditPatientModalProps {
  isOpen: boolean;
  patient: Patient;
  unit?: Unit;
  onClose: () => void;
}

const schema = z.object({
  unit: z.number().nullable(),
  nextPFADate: z.date().nullable(),
});

type EditPatientFormData = z.infer<typeof schema>;

export function EditPatientModal({
  isOpen,
  patient,
  unit,
  onClose,
}: EditPatientModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pushToast } = useToastStore();

  const { data } = useQuery({
    queryFn: () => getUnits({ page: 0, per_page: -1 }),
    queryKey: ["getUnits", { page: 0, pageSize: -1 }],
  });

  const { control, handleSubmit, setValue, watch, reset } =
    useForm<EditPatientFormData>({
      resolver: zodResolver(schema),
      defaultValues: {
        unit: unit?.id ?? null,
        nextPFADate: patient.next_test_date
          ? parseBackendDate(patient.next_test_date)
          : undefined,
      },
    });

  const resetFormValues = () => {
    reset({ unit: undefined, nextPFADate: undefined });
    setValue("unit", unit?.id ?? null);
    if (patient.next_test_date) {
      setValue("nextPFADate", parseBackendDate(patient.next_test_date));
    }
  };

  useEffect(resetFormValues, [patient, setValue, unit, reset]);

  const { mutate } = useMutation({
    mutationFn: updatePatient,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["getPatients"] });
      void queryClient.invalidateQueries({
        queryKey: ["getUnitPatients", { unitId: unit?.id ?? undefined }],
      });
      onClose();
      void pushToast({
        type: "success",
        message: t("patients.edit_patient_information_success"),
      });
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const onSubmit: SubmitHandler<EditPatientFormData> = (data) => {
    mutate({
      unitId: data.unit,
      patientId: patient.id,
      nextTestDate: data.nextPFADate,
    });
  };

  const units =
    data?.data.map((unit) => ({ label: unit.name, id: unit.id })) ?? [];

  const selectedUnitId = watch("unit");
  const selectedUnit = data?.data.find((u) => u.id === selectedUnitId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      className="self-start pt-24"
      title={t("patients.edit_patient_information")}
    >
      <div className="mb-4 flex w-full flex-row gap-4 overflow-hidden">
        <UneditableInput
          label={t("patients.full_name")}
          className="flex-1"
          text={`${patient.name} ${patient.surname}`}
        />
        <UneditableInput
          label={t("patients.date_of_birth")}
          className="flex-1"
          text={formatBackendDate(
            patient.date_of_birth,
            t("common.date_format_month_first"),
          )}
        />
      </div>

      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="unit"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SearchableSingleSelect
              id="unit-combobox"
              label={t("units.unit")}
              options={units}
              placeholder={t("unit_detail.search_for_a_unit")}
              value={value}
              onChange={onChange}
            />
          )}
        />

        <Controller
          name="nextPFADate"
          control={control}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label={t("unit_detail.next_pfa_exam")}
              id="next-pfa-date-picker"
              value={value ?? undefined}
              onChange={onChange}
            />
          )}
        />

        {unit && selectedUnit && selectedUnit.id !== unit.id && (
          <Warning className="mt-2">
            <span>
              {t("unit_detail.reassignment_warning", {
                name: `${patient.name} ${patient.surname}`,
                previousUnit: unit.name,
                currentUnit: selectedUnit.name,
                interpolation: { escapeValue: false },
              })}
            </span>
          </Warning>
        )}

        <div className="mt-2 flex w-full gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            text={t("general.cancel")}
            className="flex w-full"
          />

          <Button
            type="submit"
            variant="filled"
            text={t("general.save_changes")}
            className="flex w-full"
          />
        </div>
      </form>
    </Modal>
  );
}
